/* eslint-disable no-param-reassign */
/* eslint-disable no-underscore-dangle */
/* eslint-disable no-undef */
import calliffn from 'calliffn'
import { defaults } from './defaults'
import { EventsBus } from './EventsBus'
import { emit, subscribe } from './events'

const SCRIPTS_STATUS = '__GMANAGER_SCRIPTS_STATUS'
const LOADED_EVENT_NAME = 'GMANAGER_SCRIPTS_LOADED'

export class GoogleMap {
  /**
   * @param {HTMLElement} container
   */
  constructor(container, config = {}) {
    this.mapElement = container
    this.config = Object.assign({}, defaults, config)
    this.events = new EventsBus()
    this.gmap = null
    this.gmarkers = []

    this.state = {
      infoWindow: {
        initialized: false,
        items: [],
      },
    }

    if (!this.config.apiKey) {
      throw Error('Google Maps API key not provided.')
    }

    this.registerEvents()
    this.injectScript()
  }

  /**
   * @returns {void}
   */
  mount() {
    if (this.config.bounds) {
      this.gbounds = this.constructor.getLatLngBounds()
    }

    this.gmap = new google.maps.Map(this.mapElement, this.config.mapOptions)
    this.appendMarkers()
    this.setMapPosition()

    if (this.config.clusterer.enabled) {
      this.gclusterer = new MarkerClusterer(this.gmap, this.gmarkers, this.config.clusterer.config)
    }

    this.events.emit('didMount', { instance: this })
  }

  /**
   *
   * @private
   */
  setMapPosition() {
    if (this.config.bounds) {
      this.fitBounds()
    } else {
      this.setCenter()
    }

    this.setZoom(this.config.zoom)
  }

  /**
   * @returns {void}
   */
  appendMarkers(markers = this.config.markers) {
    markers.forEach(marker => this.appendMarker(marker))
  }

  /**
   * @param {HTMLElement} marker
   * @returns {void}
   */
  appendMarker(marker) {
    const gmarker = this.createMarker(marker)
    this.gmarkers.push(gmarker)

    if (this.config.bounds) {
      this.gbounds.extend(gmarker.position)
    }
  }

  /**
   * @param {object} config
   * @returns {object} new google.maps.Marker
   */
  createMarker(config) {
    const { coordinates, ...rest } = config
    const marker = new google.maps.Marker({
      ...rest,
      position: new google.maps.LatLng(coordinates.latitude, coordinates.longitude),
      map: this.gmap,
      icon: this.config.markerIcon ? calliffn(this.config.markerIcon, config) : this.config.defaultMarker,
    })

    this.events.emit('onMarkerCreate', { marker, config, instance: this })

    return marker
  }

  /**
   * @param {*} bounds
   * @returns {void}
   */
  fitBounds(bounds = this.gbounds) {
    this.gmap.fitBounds(bounds)
  }

  /**
   * Deletes all markers in the array by removing references to them.
   *
   * @returns {void}
   *
   * @private
   */
  deleteMarkers() {
    this.gmarkers.forEach(marker => marker.setMap(null))
    this.gmarkers = []

    if (this.config.bounds) {
      this.gbounds = this.constructor.getLatLngBounds()
    }

    if (this.config.clusterer.enabled) {
      this.gclusterer.removeMarkers(this.gclusterer.getMarkers())
      this.gclusterer.unbindAll()
    }
  }

  /**
   * Replace all markers on the map
   *
   * @param {object[]} markers
   * @returns {void}
   *
   * @public
   */
  setMarkers(markers) {
    this.deleteMarkers()
    this.appendMarkers(markers)

    if (this.config.clusterer.enabled) {
      this.gclusterer.addMarkers(this.gmarkers)
    }
  }

  setCenter(coordinates = null) {
    if (coordinates) {
      this.gmap.setCenter(this.constructor.getLatLng(coordinates.latitude, coordinates.longitude))
    } else if (this.config.center) {
      this.gmap.setCenter(this.constructor.getLatLng(this.config.center.latitude, this.config.center.longitude))
    } else {
      throw Error('Map center position not provided.')
    }
  }

  setZoom(value) {
    this.gmap.setZoom(value)
  }

  registerEvents() {
    const keys = Object.keys(this.config.events)
    keys.forEach(key => {
      if (Array.isArray(this.config.events[key])) {
        this.config.events[key].forEach(callback => this.events.on(key, callback))
      } else {
        this.events.on(key, this.config.events[key])
      }
    })
  }

  /**
   * @returns {object} new google.maps.LatLngBounds
   */
  static getLatLngBounds() {
    return new google.maps.LatLngBounds()
  }

  /**
   * @param {number} latitude
   * @param {number} longitude
   * @returns {object} new google.maps.LatLng
   */
  static getLatLng(latitude, longitude) {
    return new google.maps.LatLng(latitude, longitude)
  }

  getMarkers() {
    return this.gmarkers
  }

  infoWindow(marker, config) {
    const infowindow = new google.maps.InfoWindow(config)
    this.state.infoWindow.items.push({ instance: infowindow, marker })

    marker.addListener('click', () => {
      if (marker.open) {
        marker.open = false
        infowindow.close()
      } else {
        marker.open = true
        infowindow.open(this.gmap, marker)
      }

      this.state.infoWindow.items.forEach(item => {
        if (marker !== item.marker) {
          item.instance.close()
          item.marker.open = false
        }
      })
    })

    if (!this.infoWindow.initialized) {
      google.maps.event.addListener(this.gmap, 'click', () => {
        this.state.infoWindow.items.forEach(item => {
          item.instance.close()
          item.marker.open = false
        })
      })

      this.infoWindow.initialized = true
    }
  }

  /**
   * Inject Google Maps script and/or MarkerClusterer
   */
  injectScript() {
    if (window[SCRIPTS_STATUS] === 'loading') {
      subscribe(LOADED_EVENT_NAME, () => this.mount(), true)
    } else if (window[SCRIPTS_STATUS] === 'loaded') {
      this.mount()
    } else {
      window[SCRIPTS_STATUS] = 'loading'

      const scripts = [this.constructor.loadScript(`https://maps.googleapis.com/maps/api/js?key=${this.config.apiKey}`)]

      if (this.config.clusterer.enabled) {
        scripts.push(
          this.constructor.loadScript(
            'https://unpkg.com/@google/markerclustererplus@5.1.3/dist/markerclustererplus.min.js',
          ),
        )
      }

      Promise.all(scripts).then(() => {
        this.mount()
        emit(LOADED_EVENT_NAME)
        window[SCRIPTS_STATUS] = 'loaded'
      })
    }
  }

  /**
   * Add asnyc script to body
   *
   * @param {string} link
   * @returns {Promise}
   */
  static loadScript(link) {
    const script = document.createElement('script')
    script.setAttribute('src', link)
    script.setAttribute('type', 'text/javascript')
    script.setAttribute('async', true)
    script.setAttribute('defer', true)
    document.head.appendChild(script)

    return new Promise((resolve, reject) => {
      script.addEventListener('load', resolve)
      script.addEventListener('error', reject)
    })
  }
}
